import React, { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { JsonListChips, KeyValueGrid, SectionCard, StatusChip, formatCurrency, formatDateTime, normalizeJsonObject, safeArray, toTitleCase } from "../coreNetworkUiShared";

function buildPayload(form) {
  return {
    status: form.status,
    role: form.role,
    visibility: form.visibility,
    allowedRequestTypesJson: String(form.allowedRequestTypesText || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean),
    rateJson: {
      typicalPrice: form.typicalPrice ? Number(form.typicalPrice) : null,
      currency: form.currency || "CAD",
      budgetTier: form.budgetTier || null,
      notes: form.rateNotes || null,
    },
    isEliteCoreNetworkDecorator: form.isEliteCoreNetworkDecorator,
    sourceType: form.sourceLinkMode === "existing" ? "decorator" : null,
    sourceId: form.sourceLinkMode === "existing" && form.linkedDecoratorId ? form.linkedDecoratorId : null,
    createLinkedDecorator: form.isEliteCoreNetworkDecorator && form.sourceLinkMode === "create_new",
    linkedDecoratorPayload:
      form.isEliteCoreNetworkDecorator && form.sourceLinkMode === "create_new"
        ? {
            title: form.newDecoratorTitle || null,
            CurrencyCode: form.newDecoratorCurrency || form.currency || "CAD",
          }
        : null,
  };
}

const emptyForm = {
  status: "active",
  role: "designer",
  visibility: "internal",
  allowedRequestTypesText: "",
  typicalPrice: "",
  currency: "CAD",
  budgetTier: "",
  rateNotes: "",
  isEliteCoreNetworkDecorator: false,
  sourceLinkMode: "none",
  linkedDecoratorId: null,
  newDecoratorTitle: "",
  newDecoratorCurrency: "CAD",
};

export default function CoreNetworkMembershipPanel({
  memberships = [],
  onCreateMembership,
  saving = false,
  canManagePayment = false,
  canManagePlacement = false,
  member = null,
  decoratorOptions = [],
}) {
  const [form, setForm] = useState(emptyForm);

  const sortedMemberships = useMemo(
    () => [...safeArray(memberships)].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
    [memberships]
  );

  const setField = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: field === "isEliteCoreNetworkDecorator" ? event.target.checked : event.target.value,
    }));
  };

  const handleCreate = async () => {
    await onCreateMembership?.(buildPayload(form));
    setForm(emptyForm);
  };

  const hasExistingDecoratorLink = Boolean(member?.linkedDecorator?.id || (member?.sourceType === "decorator" && member?.sourceId));
  const selectedDecorator =
    decoratorOptions.find((option) => Number(option.id) === Number(form.linkedDecoratorId)) || null;
  const selectedDecoratorIsLocalOnly =
    form.isEliteCoreNetworkDecorator &&
    form.sourceLinkMode === "existing" &&
    selectedDecorator &&
    selectedDecorator.isEliteDecorator !== true;
  const approvalBlocked =
    form.isEliteCoreNetworkDecorator &&
    !hasExistingDecoratorLink &&
    form.sourceLinkMode === "none";
  const canSubmit =
    !approvalBlocked &&
    !selectedDecoratorIsLocalOnly &&
    (!form.isEliteCoreNetworkDecorator || hasExistingDecoratorLink || form.sourceLinkMode !== "none");

  return (
    <SectionCard
      title="Memberships"
      subtitle="Instance-specific access and working terms for this talent profile."
    >
      {sortedMemberships.length ? (
        <Stack spacing={2.5} sx={{ mb: 3 }}>
          {sortedMemberships.map((membership) => {
            const rateJson = normalizeJsonObject(membership.rateJson);
            return (
              <Grid container spacing={2} key={membership.id} sx={{ p: 2, borderRadius: 2, border: "1px solid #edf1f7" }}>
                <Grid item xs={12} md={4}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <StatusChip value={membership.status} />
                      <Typography variant="subtitle2">{toTitleCase(membership.role)}</Typography>
                      {membership.isEliteCoreNetworkDecorator ? <StatusChip value="elite decorator" /> : null}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Created {formatDateTime(membership.createdAt)}
                    </Typography>
                    <JsonListChips values={membership.allowedRequestTypesJson} emptyLabel="No request restrictions" />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={8}>
                  <KeyValueGrid
                    items={[
                      ...(canManagePlacement ? [{ label: "Visibility", value: toTitleCase(membership.visibility) }] : []),
                      ...(canManagePayment
                        ? [
                            {
                              label: "Rate",
                              value: rateJson.typicalPrice ? formatCurrency(rateJson.typicalPrice, rateJson.currency || "CAD") : "-",
                            },
                            { label: "Budget tier", value: rateJson.budgetTier ? toTitleCase(rateJson.budgetTier) : "-" },
                            { label: "Rate notes", value: rateJson.notes || "-" },
                          ]
                        : []),
                    ]}
                  />
                </Grid>
              </Grid>
            );
          })}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          No memberships are attached to this instance yet.
        </Typography>
      )}

      {onCreateMembership ? (
        <Stack spacing={2}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Add membership
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField fullWidth select label="Status" value={form.status} onChange={setField("status")}>
                {["pending", "active", "paused", "rejected", "suspended"].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Role" value={form.role} onChange={setField("role")} />
            </Grid>
            <Grid item xs={12} md={3}>
              {canManagePlacement ? (
                <TextField fullWidth select label="Visibility" value={form.visibility} onChange={setField("visibility")}>
                  {["private", "internal", "marketplace", "featured"].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Allowed request types" value={form.allowedRequestTypesText} onChange={setField("allowedRequestTypesText")} helperText="Comma-separated" />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.isEliteCoreNetworkDecorator}
                    onChange={setField("isEliteCoreNetworkDecorator")}
                  />
                }
                label="Is Elite Core Decorator"
              />
            </Grid>
            {form.isEliteCoreNetworkDecorator ? (
              <>
                {hasExistingDecoratorLink ? (
                  <Grid item xs={12}>
                    <Alert severity="success">
                      Linked to Decorator: {member?.linkedDecorator?.title || `Decorator #${member?.sourceId}`}
                    </Alert>
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth select label="Decorator source" value={form.sourceLinkMode} onChange={setField("sourceLinkMode")}>
                        <MenuItem value="none">Choose a decorator source</MenuItem>
                        <MenuItem value="existing">Link existing Elite Core Decorator</MenuItem>
                        <MenuItem value="create_new">Create as new Core Network decorator</MenuItem>
                      </TextField>
                    </Grid>
                    {form.sourceLinkMode === "existing" ? (
                      <>
                        <Grid item xs={12} md={8}>
                          <Autocomplete
                            options={decoratorOptions}
                            value={selectedDecorator}
                            onChange={(_event, value) =>
                              setForm((prev) => ({
                                ...prev,
                                linkedDecoratorId: value?.id || null,
                              }))
                            }
                            getOptionLabel={(option) => option?.title || `Decorator ${option?.id || ""}`}
                            isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Existing Decorator"
                                helperText="Select the operational Decorator record to approve this provider for production work."
                              />
                            )}
                          />
                        </Grid>
                        {selectedDecoratorIsLocalOnly ? (
                          <Grid item xs={12}>
                            <Alert severity="warning">
                              This decorator is currently local/private. Promote to Elite Decorator before using in Core Network.
                            </Alert>
                          </Grid>
                        ) : null}
                      </>
                    ) : null}
                    {form.sourceLinkMode === "create_new" ? (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField fullWidth label="New Decorator title" value={form.newDecoratorTitle} onChange={setField("newDecoratorTitle")} helperText="Defaults to company or display name if left blank." />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField fullWidth label="Decorator currency" value={form.newDecoratorCurrency} onChange={setField("newDecoratorCurrency")} />
                        </Grid>
                        <Grid item xs={12}>
                          <Alert severity="info">
                            A linked Elite Core Decorator record will be created when this membership is approved.
                          </Alert>
                        </Grid>
                      </>
                    ) : null}
                    {approvalBlocked ? (
                      <Grid item xs={12}>
                        <Alert severity="warning">
                          Approved decoration providers must either link to an existing Decorator or explicitly create a new linked Decorator record.
                        </Alert>
                      </Grid>
                    ) : null}
                  </>
                )}
              </>
            ) : null}
            {canManagePayment ? (
              <>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="Budget tier" value={form.budgetTier} onChange={setField("budgetTier")} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="Typical price" value={form.typicalPrice} onChange={setField("typicalPrice")} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField fullWidth label="Currency" value={form.currency} onChange={setField("currency")} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="Rate notes" value={form.rateNotes} onChange={setField("rateNotes")} />
                </Grid>
              </>
            ) : null}
          </Grid>
          <Box>
            <Button variant="outlined" onClick={handleCreate} disabled={saving || !canSubmit}>
              {saving ? "Saving..." : "Add membership"}
            </Button>
          </Box>
        </Stack>
      ) : null}
    </SectionCard>
  );
}
